import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ThemeProvider,
  createTheme
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import React, { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { ChangeDocument, MachineDocumentState } from "../../models/machineDocumentsModel";
import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import { changeDocument, getDocuments } from "../../customerMachineDocumentsSlice";
import DocumentFileItem from "./DocumentFileItem";
import useTranslation from "../../../../shared/hooks/useTranslation";

export interface ChangeDocumentDialogProps {
  open: boolean;
  onClose: () => void;
  machineId: string;
}

const ChangeDocumentDialog = ({ open, onClose, machineId }: ChangeDocumentDialogProps) => {
  const { document } = useAppSelector<MachineDocumentState>((state: any) => state.customerMachineDocuments);
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("myMachinery");
  const { translate: tCommon } = useTranslation("common");

  const documentId = document?.id || undefined;

  const {
    register,
    setValue,
    handleSubmit,
    reset,
    clearErrors,
    formState: { errors }
  } = useForm<{ documents: ChangeDocument[] }>();

  const onSubmit: SubmitHandler<{ documents: ChangeDocument[] }> = (data: { documents: ChangeDocument[] }) => {
    handleCreateOrEditDocument(data.documents);
  };

  const handleCreateOrEditDocument = async (model: ChangeDocument[]) => {
    await dispatch(changeDocument({ documentId, model: model[0] }));
    await dispatch(getDocuments(machineId));
    onClose();
  };

  useEffect(() => {
    const changeDocument: { documents: ChangeDocument[] } = {
      documents: [
        {
          name: document?.name || "",
          file: document?.file || null
        }
      ]
    };
    reset(changeDocument);
  }, [document]);

  const theme = createTheme({
    components: {
      MuiDialog: {
        styleOverrides: {
          root: {
            ".MuiDialogContent-root": {
              padding: "0 24px 20px"
            },

            ".MuiDialogTitle-root": {
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center"
            },

            ".MuiPaper-root": {
              minWidth: "480px",
              display: "flex",
              justifyContent: "center"
            }
          }
        }
      }
    }
  });

  return (
    <ThemeProvider theme={theme}>
      <Dialog open={open} onClose={onClose}>
        <DialogTitle>
          <p className="heading-2">{translate("editUploadedFile")}</p>
        </DialogTitle>

        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 14,
            top: 14,
            color: "#333333"
          }}
        >
          <CloseIcon />
        </IconButton>

        <DialogContent>
          <form id="changeDocument" onSubmit={handleSubmit(onSubmit)}>
            <Box className="flex flex-col gap-2">
              <DocumentFileItem
                document={document?.file || null}
                documentIndex={0}
                register={register}
                setValue={setValue}
                isCreate={false}
                errors={errors}
                clearErrors={clearErrors}
              />
            </Box>
          </form>
        </DialogContent>

        <DialogActions className="flex">
          <Button variant="outlined" className="w-full" onClick={onClose}>
            {tCommon("buttons.cancel")}
          </Button>

          <Button form="changeDocument" type="submit" variant="contained" className="w-full">
            {tCommon("buttons.save")}
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default ChangeDocumentDialog;